import React, { FC, useContext, useEffect, useMemo, useState } from 'react';

import { useMutation } from '@apollo/client';
import {
  Button,
  H1,
  InfoIcon,
  Modal,
  Paragraph,
  Preloader,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import { parseCookies, setCookie } from 'nookies';

import { LocalizationContext } from 'localization';
import {
  CREATE_HOST_PIN,
  ICreateHostPINRequest,
  ICreateHostPINResponse,
} from 'schemas';

import s from './GeneratePinModal.module.scss';
import { IGeneratePinModalProps } from './interfaces';

export const GeneratePinModal: FC<IGeneratePinModalProps> = ({
  hostId,
  hosts,
  onClose,
}) => {
  const keyPin = `pin-${hostId}`;
  const cookies: Record<string, string | undefined> = parseCookies();

  const { t } = useContext(LocalizationContext);
  const [type, setType] = useState<'loading' | 'error' | 'success'>('loading');
  const [pin, setPin] = useState(cookies?.[keyPin] || '');

  const currentHost = hosts?.hostsOverview?.find((host) => host.id === hostId);
  const currentGroupTitle = hosts?.hostGroupsOverview?.find(
    (group) => group.id === currentHost?.group_id
  );

  const [generatePin, { data }] = useMutation<
    ICreateHostPINResponse,
    ICreateHostPINRequest
  >(CREATE_HOST_PIN, {
    onError: () => {
      setType('error');
    },
  });

  useEffect(() => {
    if (!pin) {
      generatePin({ variables: { hostId } });
    } else {
      setType('success');
    }
  }, [pin]);

  useEffect(() => {
    if (!data) {
      return;
    } else {
      setCookie(null, keyPin, data.createHostPIN, {
        maxAge: 3600,
        secure: false,
      });
      setPin(data.createHostPIN);
      setType('success');
    }
  }, [data]);

  const text = useMemo(() => {
    switch (type) {
      case 'loading':
        return t('generate.pin.modal.text.loaded');
      case 'error':
        return t('generate.pin.modal.text.error');
      case 'success':
        return t('generate.pin.modal.text.success');
    }
  }, [type, t]);

  return (
    <Modal className={s.container} onClose={onClose}>
      <div className={s.header}>
        <H1>
          {t(
            'generate.pin.modal.title',
            `${currentHost?.alias}`,
            `${currentGroupTitle?.title}`
          )}
        </H1>
        <Paragraph color="secondary" bold>
          {text}
        </Paragraph>
      </div>
      <div className={s.body}>
        <Preloader loading={type === 'loading'}>
          {type === 'success' && pin ? (
            <Paragraph size="l" bold>
              {`${pin.slice(0, 3)}-${pin.slice(3)}`}
            </Paragraph>
          ) : (
            <InfoIcon color="error" className={s.icon} />
          )}
        </Preloader>
        <Button
          iconName="RestartIcon"
          className={s.refresh}
          variant="ghost"
          onClick={() => generatePin({ variables: { hostId } })}
        />
      </div>
      <Button
        variant="secondary"
        fullWidth
        size="l"
        className={cn(type === 'loading' && s.hide)}
        onClick={onClose}
      >
        {t('close')}
      </Button>
    </Modal>
  );
};
